import { Component, EventEmitter, Output, OnDestroy } from '@angular/core'
import { ClusteringNormalization, PipelineType, TsneParameters } from '../../../shared/model/jobs'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { combineLatest, Subject, take, takeUntil } from 'rxjs'
import { Store } from '@ngrx/store'
import { plotDataActions } from '../../../state/plot-data/plot-data.actions'
import {
    selectCaseMarkers,
    selectLoadedSegObjectPropertyMaps,
    selectMatchedTsneResults,
} from '../../../state/case/case.selectors'
import { SegObjectPropertyMap } from '../../../state/case/case.state'
import { selectJobs } from '../../../state/job/job.selectors'
import { environment } from 'src/environments/environment'
import { JobApiService } from 'src/app/shared/services/job-api.service'
import { closeJobSelectionDialog } from 'src/app/shared/utils/job-selection-dialog-utils'
import { HttpErrorResponse } from '@angular/common/http'
import { ToasterService } from '../../../shared/services/toaster.service'
import { MarkerCatalogService } from '../../../shared/services/marker-catalog.service'
import { selectImageLayers, selectAnnotationLayers } from 'src/app/state/viewer/viewer.selectors'

@Component({
    selector: 'app-tsne-job',
    templateUrl: './tsne-job.component.html',
    styleUrls: ['./tsne-job.component.scss'],
})
export class TsneJobComponent implements OnDestroy {
    ClusteringNormalizationValues = Object.values(ClusteringNormalization)

    tsneForm: FormGroup

    formIsVisible = false
    createJobMode = true
    readonly clusteringMarkers$ = this.store.select(selectCaseMarkers)
    readonly matchedTsneResults$ = this.store.select(selectMatchedTsneResults)
    readonly imageLayers$ = this.store.select(selectImageLayers)
    readonly annotationLayers$ = this.store.select(selectAnnotationLayers)
    readonly SELECTED_EXPERIMENT_NAME_KEY = 'selectedExperimentName'
    readonly NEW_EXPERIMENT_NAME_KEY = 'newExperimentName'
    readonly LAYER_TYPE_MARKERS_KEY = 'markers'
    readonly LAYER_TYPE_TRANSCRIPTS_KEY = 'transcripts'
    readonly LAYER_TYPE_MORPHOMETRICS_KEY = 'morphometrics'
    readonly PERPLEXITY_FORM_KEY = 'perplexity'
    readonly NUMBER_OF_ITERATIONS_FORM_KEY = 'numberOfIterations'
    readonly NORMALIZATION_FORM_KEY = 'normalization'

    experiments = [{ name: 'Create new', x: 'TSNE1_X', y: 'TSNE1_Y' }]

    layerTypes: { name: string; formControl: string; disabled: boolean }[] = []

    private readonly destroy$ = new Subject<void>()
    jobIsRunning = false
    private readonly wikiUrl = environment.wikiUrl
    readonly loadedSegObjectPropertyMaps$ = this.store.select(selectLoadedSegObjectPropertyMaps)
    private loadedIntensityMaps: SegObjectPropertyMap[] = []

    constructor(
        private readonly formBuilder: FormBuilder,
        private readonly store: Store,
        private readonly jobApi: JobApiService,
        private readonly toaster: ToasterService,
        private readonly markerCatalog: MarkerCatalogService
    ) {
        this.buildForm(this.experiments[0])

        this.store.select(selectJobs).subscribe(jobs => {
            const job = jobs.find(
                j => j.type === 'TSN' && j.status !== 'Rejected' && j.status !== 'Closed'
            )
            this.jobIsRunning = job ? true : false
        })

        combineLatest([this.loadedSegObjectPropertyMaps$, this.matchedTsneResults$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([maps, tsne]) => {
                this.loadedIntensityMaps = maps
                this.experiments = [
                    { name: 'Create new', x: 'TSNE1_X', y: 'TSNE1_Y' },
                    ...tsne.map(t => ({
                        name: t.name,
                        x: t.x,
                        y: t.y,
                        perplexity: t.perplexity,
                        numberOfIterations: t.number_of_iterations,
                        normalization: t.normalization,
                        markers: t.markers,
                        transcripts: t.transcripts,
                        morphometrics: t.morphometrics,
                    })),
                ]
                this.ensureValidExperimentSelection()
            })

        combineLatest([this.imageLayers$, this.annotationLayers$])
            .pipe(take(1))
            .subscribe(([imageLayers, annotationLayers]) => {
                this.layerTypes = [
                    {
                        name: 'Marker',
                        formControl: this.LAYER_TYPE_MARKERS_KEY,
                        disabled: imageLayers.length === 0,
                    },
                    {
                        name: 'Transcripts',
                        formControl: this.LAYER_TYPE_TRANSCRIPTS_KEY,
                        disabled: annotationLayers.filter(l => l.name !== 'selection').length === 0,
                    },
                    {
                        name: 'Morphometrics',
                        formControl: this.LAYER_TYPE_MORPHOMETRICS_KEY,
                        disabled: false,
                    },
                ]
            })
    }

    ngOnDestroy(): void {
        this.destroy$.next()
        this.destroy$.complete()
    }

    @Output() paramsSet = new EventEmitter<TsneParameters>()

    get paramsValid(): boolean {
        if (
            !this.tsneForm.value.markers &&
            !this.tsneForm.value.transcripts &&
            !this.tsneForm.value.morphometrics
        ) {
            return false
        }
        return this.tsneForm.valid
    }

    /** Field validation lines (also used to build the Start Job tooltip) */
    get validationSummaryMessages(): string[] {
        const msgs: string[] = []
        if (!this.tsneForm) return msgs

        const selectedCtl = this.tsneForm.get(this.SELECTED_EXPERIMENT_NAME_KEY)
        if (selectedCtl?.hasError('required')) {
            msgs.push('Choose an experiment.')
        }

        if (this.createJobMode) {
            const newNameCtl = this.tsneForm.get(this.NEW_EXPERIMENT_NAME_KEY)
            if (newNameCtl?.hasError('required')) {
                msgs.push('Enter an experiment name.')
            }
        }

        const perplexityCtl = this.tsneForm.get(this.PERPLEXITY_FORM_KEY)
        if (perplexityCtl?.hasError('required')) {
            msgs.push('Enter perplexity.')
        } else if (perplexityCtl?.hasError('min')) {
            msgs.push('Perplexity must be at least 1.')
        }

        const iterationsCtl = this.tsneForm.get(this.NUMBER_OF_ITERATIONS_FORM_KEY)
        if (iterationsCtl?.hasError('required')) {
            msgs.push('Enter number of iterations.')
        } else if (iterationsCtl?.hasError('min')) {
            msgs.push('Number of iterations must be at least 250.')
        }

        return msgs
    }

    /** Shown as native tooltip when Start Job is disabled or job running */
    get startJobTooltip(): string | null {
        if (!this.formIsVisible || !this.tsneForm) {
            return null
        }
        if (this.jobIsRunning) {
            return 'A t-SNE job is already running.'
        }
        if (!this.createJobMode) {
            return 'Select "Create new" and enter an experiment name to start a job.'
        }
        if (this.paramsValid) {
            return null
        }
        const msgs: string[] = [...this.validationSummaryMessages]
        const v = this.tsneForm.value
        if (!v.markers && !v.transcripts && !v.morphometrics) {
            msgs.push('Select at least one input feature.')
        }
        return msgs.length > 0 ? msgs.join('\n') : null
    }

    confirmParams(): void {
        if (!this.paramsValid || !this.createJobMode) return
        if (this.loadedIntensityMaps.length === 0) {
            this.toaster.httpError(
                new HttpErrorResponse({ error: 'Select an intensity map' }),
                ''
            )
            return
        }
        const {
            selectedExperimentName: _selected,
            newExperimentName,
            ...formFields
        } = this.tsneForm.getRawValue()
        const inputFeature = ['markers', 'morphometrics', 'transcripts'].filter(
            feature => formFields[feature]
        )
        this.jobApi
            .startClusteringJob({
                type: PipelineType.tsneClustering,
                ...formFields,
                newExperimentName,
                input_feature: inputFeature,
                seg_object_property_maps: this.loadedIntensityMaps,
            })
            .subscribe({
                next: () => closeJobSelectionDialog(),
                error: (err: HttpErrorResponse) => {
                    this.toaster.httpError(err, 'Error starting job')
                },
            })
        this.jobIsRunning = true
    }

    displayForm(): void {
        this.formIsVisible = !this.formIsVisible
        this.createJobMode = true
        if (this.formIsVisible) {
            this.buildForm(this.experiments[0])
        }
    }

    setExperiment(experimentName: string): void {
        if (experimentName === 'Create new') {
            this.createJobMode = true
            this.setNewExperimentNameRequired(true)
            return
        }
        const experiment = this.experiments.find(ex => ex.name === experimentName)
        if (!experiment) return
        this.createJobMode = false
        this.buildForm(experiment)
        this.setNewExperimentNameRequired(false)
        this.setDataAxis(experiment.x, experiment.y)
    }

    buildForm(experiment: any) {
        const createMode = !experiment?.name || experiment.name === 'Create new'
        this.createJobMode = createMode
        this.tsneForm = this.formBuilder.group({
            [this.SELECTED_EXPERIMENT_NAME_KEY]: new FormControl(experiment?.name ?? 'Create new', [
                Validators.required,
            ]),
            [this.NEW_EXPERIMENT_NAME_KEY]: new FormControl(
                '',
                createMode ? [Validators.required] : []
            ),
            [this.LAYER_TYPE_MARKERS_KEY]: new FormControl(experiment?.markers),
            [this.LAYER_TYPE_TRANSCRIPTS_KEY]: new FormControl(experiment?.transcripts),
            [this.LAYER_TYPE_MORPHOMETRICS_KEY]: new FormControl(experiment?.morphometrics),
            [this.PERPLEXITY_FORM_KEY]: new FormControl(experiment?.perplexity, [
                Validators.required,
                Validators.min(1),
            ]),
            [this.NUMBER_OF_ITERATIONS_FORM_KEY]: new FormControl(experiment?.numberOfIterations, [
                Validators.required,
                Validators.min(250),
            ]),
            [this.NORMALIZATION_FORM_KEY]: new FormControl(experiment?.normalization),
        })
    }

    private ensureValidExperimentSelection(): void {
        if (!this.tsneForm) return
        const selected = this.tsneForm.get(this.SELECTED_EXPERIMENT_NAME_KEY)?.value
        if (this.experiments.some(experiment => experiment.name === selected)) return
        this.createJobMode = true
        this.tsneForm.patchValue(
            { [this.SELECTED_EXPERIMENT_NAME_KEY]: 'Create new' },
            { emitEvent: false }
        )
        this.setNewExperimentNameRequired(true)
    }

    private setNewExperimentNameRequired(required: boolean): void {
        const control = this.tsneForm?.get(this.NEW_EXPERIMENT_NAME_KEY)
        if (!control) return
        control.setValidators(required ? [Validators.required] : [])
        control.updateValueAndValidity({ emitEvent: false })
    }

    setDataAxis(markerXAxis: string, markerYAxis: string): void {
        const markerX = this.markerCatalog.getFullMarker(markerXAxis)
        const markerY = this.markerCatalog.getFullMarker(markerYAxis)
        if (!markerX || !markerY) {
            this.toaster.error(
                `Could not load plot axes "${markerXAxis}" and "${markerYAxis}".`,
                'Error'
            )
            return
        }

        this.store.dispatch(
            plotDataActions.loadDataBothAxes({
                markerXAxis: markerX,
                markerYAxis: markerY,
            })
        )
    }

    goToWiki(): void {
        window.open(`${this.wikiUrl}home/Working_with_jobs#h-7-umap-and-t-sne`, '_blank')
    }
}
